<script setup lang="ts">
  import {computed, onMounted, ref, toRefs, type PropType} from "vue";
  import SelectDropdown from "@/components/SelectDropdown.vue";
  import {useCartStore, militaryStates, type Address, type AddressFilled, type LineItem} from "@/stores/cartStore";
  import IconConfirm from "@/icons/IconConfirm.vue";
  import IconLoader from "@/icons/IconLoader.vue";
  import { v4 as uuidv4 } from 'uuid';
  import {areAddressesSame} from "@/utils/compareAddress";
  import AddressCorrectionPopup from "@/components/AddressCorrectionPopup.vue";
  import { fetchWrapper } from "@/utils/fetchWrapper";

  const properties = defineProps<{
    selectedAddress: string | "+",
    lineItem: LineItem,
  }>();
  const { lineItem, selectedAddress } = toRefs(properties);
  const emit = defineEmits(['closeAddressForm'])
  const cartState = useCartStore();
  const submitAction = ref(false);
  const address1_ref = ref(null);
  const address1 = ref<AddressFilled | null>(null);
  const address2 = ref<AddressFilled | null>(null);
  const formLoaded = ref(false);
  const showSuggestedModal = ref(false);
  const validationFailed = ref(false);

  const countries = computed(() => {
    if(!cartState.countries.length) {
      return [];
    }

    return cartState.countries.filter(i => i.code === "US").map(i => ({
      label: i.name,
      value: i.code,
      removable: false
    }));
  });

  const states = computed(() => {
    if(!cartState.countries.length) {
      return [];
    }
    return (cartState.countries.find(i => i.code === "US") || {provinces: []}).provinces.filter(i => !militaryStates.includes(i.code)).map(i => ({
      label: i.name,
      value: i.code,
      removable: false
    }));
  });

  const address = ref<AddressFilled>({
    first_name: lineItem.value?.properties?._gift_first_name || "",
    last_name: lineItem.value?.properties?._gift_last_name || '',
    province_code: '',
    country: 'United States',
    country_code: 'US',
    address1: '',
    address2: '',
    phone: '',
    company: '',
    id: '',
    city: '',
    province: '',
    zip: '',
    validation_status: '',
    type: '',
  });


  const loqateMapping = [
    { element: 'address1', field: 'Line1' },
  ];

  const loqateOptions = {
    key: cartState.loqateKey,
    countries: {
      codesList: 'USA'
    },
    setCountryByIP: false
  };

  const loqateInstance = new pca.Address(JSON.parse(JSON.stringify(loqateMapping)), loqateOptions);

  onMounted(() => {
    if(selectedAddress.value !== "+" && selectedAddress.value !== null && cartState.localLineItems[lineItem.value.key]?.selectedAddress?.id === selectedAddress.value) {
      address.value = {
        ...address.value,
        ...cartState.localLineItems[lineItem.value.key]?.selectedAddress
      };
    }

    loqateInstance.listen('populate', (newAddress: {
      FormattedLine1: string;
      PostalCode: string;
      City: string;
      ProvinceCode: string;
      CountryIso2: string;
      CountryName: string;
    }, variations: any) => {
      address.value.address1 = newAddress.FormattedLine1;

      address.value.zip = newAddress.PostalCode;
      address.value.city = newAddress.City;
      address.value.province_code = militaryStates.includes(newAddress.ProvinceCode) ? '' : newAddress.ProvinceCode;
      address.value.country_code = newAddress.CountryIso2;
      address.value.country = newAddress.CountryName;
      return false;
    });
    loqateInstance.load();
  });

  const validateAddress = async (address: AddressFilled) => {
    validationFailed.value = false;
    const response = await fetchWrapper<any>(`${cartState.apiUrl}address/validate`, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
      },
      body: JSON.stringify({
        streetLines: [
          address.address1 || '',
          address.address2 || '',
        ],
        countryCode: address.country_code,
        city: address.city,
        stateOrProvinceCode: address.province_code,
        postalCode: address.zip
      }),
    }, { context: 'validateAddress', state: JSON.stringify(cartState.lineItems) });

    const data = response ? response : { success: false };

    if (data.success) {
      const vAddresses = data.response.output.resolvedAddresses;

      if (vAddresses.length && vAddresses[0].attributes && vAddresses[0].attributes.Matched === 'true') {
        const vAddress = Object.assign({}, address) as AddressFilled;
        vAddress.address1 = vAddresses[0].streetLinesToken[0];

        if (vAddresses[0].streetLinesToken.length > 1) {
          vAddress.address2 = vAddresses[0].streetLinesToken[1];
        }

        vAddress.city = vAddresses[0].city;
        vAddress.country = countries.value.find(i => i.value === vAddresses[0].countryCode)?.label || "United States";
        vAddress.country_code = vAddresses[0].countryCode;
        vAddress.province_code = vAddresses[0].stateOrProvinceCode;
        vAddress.zip = vAddresses[0].postalCode;

        if (militaryStates.includes(vAddress.province_code)) {
          validationFailed.value = true;
          return;
        }

        if (areAddressesSame(address, vAddress)) {
          vAddress.validation_status = 'Valid';
          address.validation_status = 'Valid';
        } else {
          showSuggestedModal.value = true;
          address1.value = {
            ...address,
            validation_status: 'Customer Choice'
          };
          address2.value = {
            ...vAddress,
            validation_status: vAddresses[0].attributes.InvalidSuiteNumber !== 'true' && vAddresses[0].attributes.SuiteRequiredButMissing !== 'true'  ? 'Valid' : 'Not Valid'
          };

          return;
        }
      } else {
        address.validation_status = 'Failed';
      }
    } else {
      address.validation_status = 'Failed';
    }

    cartState.saveAddress({
      isNew: selectedAddress.value === '+',
      ...address,
      id: address.id || uuidv4(),
    }, lineItem.value.key);
    emit('closeAddressForm');
  }

  const handleSubmitAddress = async () => {
    submitAction.value = true;

    if(
      address.value.first_name &&
      address.value.last_name &&
      address.value.address1 &&
      address.value.zip &&
      address.value.city &&
      address.value.province_code &&
      !militaryStates.includes(address.value.province_code) &&
      address.value.country
    ) {
      formLoaded.value = true;
      await validateAddress(address.value);
      formLoaded.value = false;
    }
  }

  const handleSelectAddress = ({ address, value }: { address: AddressFilled, value: string }) => {
    cartState.saveAddress({
      isNew: selectedAddress.value === '+',
      ...address,
      id: address.id || uuidv4(),
    }, lineItem.value.key);

    showSuggestedModal.value = false;
    emit('closeAddressForm');
  }
</script>
<template>
  <div class="CartItem__AddressForm form__hidden">
    <div class="form__group form__group--2">
      <label for="firstname_1" class="form__label">First Name</label>
      <input id="firstname_1" type="text" placeholder="First Name" class="form__input input" :class="{ 'input--error': submitAction && !address.first_name }" name="firstname" aria-label="First Name" v-model="address.first_name">
      <span class="input-text form__input-text" :class="{'input-text--error': submitAction && !address.first_name}">This is a required field</span>
    </div>

    <div class="form__group form__group--2">
      <label for="lastname_1" class="form__label">Last Name</label>
      <input id="lastname_1" type="text" placeholder="Last Name" name="lastname" aria-label="Last Name" class="form__input input" :class="{ 'input--error': submitAction && !address.last_name }" v-model="address.last_name">
      <span class="input-text form__input-text" :class="{'input-text--error': submitAction && !address.last_name}">This is a required field</span>
    </div>

    <div class="form__group">
      <label for="company_1" class="form__label">Company (optional)</label>
      <input id="company_1" type="text" placeholder="Company" name="company" aria-label="Company" class="form__input input" maxlength="70" v-model="address.company">
    </div>

    <div class="form__group">
      <label for="address1_1" class="form__label">Delivery Address</label>
      <input ref="address1_ref" autocomplete="false" id="address1_1" type="text" placeholder="Delivery Address" name="address1" aria-label="Delivery Address" class="form__input input" :class="{ 'input--error': submitAction && !address.address1 }" v-model="address.address1">
      <span class="input-text form__input-text" :class="{'input-text--error': submitAction && !address.address1}">This is a required field</span>
    </div>

    <div class="form__group">
      <label for="address2_1" class="form__label">Apartment (optional)</label>
      <input id="address2_1" type="text" placeholder="Apartment" name="address2" aria-label="Apartment" class="form__input input" v-model="address.address2">
    </div>

    <div class="form__group form__group--3">
      <label for="zip_1" class="form__label">Zip/Postal Code</label>
      <input id="zip_1" type="text" placeholder="Zip/Postal Code" name="zip" aria-label="ZIP" class="form__input input" :class="{ 'input--error': submitAction && !address.zip }" v-model="address.zip">
      <span class="input-text form__input-text" :class="{'input-text--error': submitAction && !address.zip}">Enter a valid ZIP</span>
    </div>

    <div class="form__group form__group--3">
      <label for="city_1" class="form__label">City</label>
      <input id="city_1" type="text" placeholder="City" name="city" aria-label="City" class="form__input input" :class="{ 'input--error': submitAction && !address.city }" v-model="address.city">
      <span class="input-text form__input-text" :class="{'input-text--error': submitAction && !address.city}">This is a required field</span>
    </div>

    <div class="form__group form__group--3">
      <label for="state-custom_1" class="form__label">State</label>
      <SelectDropdown
        :class-name="{ 'input--error': submitAction && (!address.province_code || militaryStates.includes(address.province_code)) }"
        :options="states"
        :placeholder="'State'"
        :modelValue="address.province_code"
        @update:modelValue="$event => (address.province_code = $event)"
      />
      <span class="input-text form__input-text" :class="{'input-text--error': submitAction && (!address.province_code || militaryStates.includes(address.province_code))}">Select a state</span>
    </div>

    <div class="form__group">
      <label for="country-custom_1-2" class="form__label">Country</label>
      <SelectDropdown
        :class-name="{ 'input--error': submitAction && !address.country_code }"
        :options="countries"
        :placeholder="'Country'"
        :modelValue="address.country_code"
        @update:modelValue="$event => {
          address.country_code = $event;
          address.country = countries.find(i => i.value === $event)?.label || 'United States';
        }"
      />
      <span class="input-text form__input-text" :class="{'input-text--error': submitAction && !address.country}">Select a country</span>
    </div>

    <div class="form__group">
      <label for="phone_1" class="form__label">Phone number (optional)</label>
      <input id="phone_1" type="text" placeholder="Phone number" name="phone" class="form__input input" v-model="address.phone">
    </div>

    <template v-if="validationFailed">
      <div class="form__group">
        <span class="Cart__message is-show input-text--error">
          The delivery cannot be made to this address.<br>
          Please correct the address or contact us: <a href="/pages/contact-us">Contact us</a>
        </span>
      </div>
    </template>

    <div class="form-button">
      <button @click="handleSubmitAddress"
              type="button"
              aria-label="Button save address"
              class="Button Button-address Button--primary Button--full"
              :class="{'is-block': formLoaded}"
              name="apply">
        <IconConfirm />
        <IconLoader />
        Save address to continue
      </button>
    </div>
  </div>
  <template v-if="showSuggestedModal">
    <teleport to="body">
      <AddressCorrectionPopup
        :address1="address1"
        :address2="address2"
        @select-address="$event => { handleSelectAddress($event); }"
        @close-address-modal="$event => (showSuggestedModal = false)"
      />
    </teleport>
  </template>
</template>